import { Fragment } from 'react';

import {
  Badge,
  Button,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { ColumnDef, CellContext, createColumnHelper, Row, Table } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  CalendarOutline,
  DEFAULT_DATE_FORMAT,
  GiftOutline,
  IndividualStatus,
  MoreCircleOutline as MoreCircle,
  InviteOutline as Invite,
} from '@aslanpay/portal-common-lib';

import {
  EmployeeActions,
  EmployeeRow,
  GiftingEmployeesBulkActions,
} from 'app/components/UI/Table/Table.types';

import { Status } from 'app/constants/individual';
import { ActionValueType, EmployeeActionsIds } from 'app/constants/table';

export const giftingEmployeeActionsValues: ActionValueType[] = [
  { id: EmployeeActionsIds.PROFILE, value: 'Go to profile' },
  { id: EmployeeActionsIds.EDIT_DETAILS, value: "Edit person's details" },
  { id: EmployeeActionsIds.INVITE, icon: Invite, value: 'Invite to Aslan' },
  { id: EmployeeActionsIds.TERMINATE, icon: CalendarOutline, value: 'Set leave date' },
  { id: EmployeeActionsIds.EDIT_TERMINATE, icon: CalendarOutline, value: 'Manage leave date' },
  { id: EmployeeActionsIds.GIFT, icon: GiftOutline, value: 'Gift people' },
];

// Gifting-specific columns without payroll-related fields
const GiftingEmployeeColumns = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Company email',
  status: 'Status',
} as const;

export function getGiftingEmployeesColumns(actions: EmployeeActions) {
  const columnHelper = createColumnHelper<EmployeeRow>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<EmployeeRow, any>[] = Object.keys(GiftingEmployeeColumns).map(
    (field) => {
      return columnHelper.accessor(field as keyof EmployeeRow, {
        cell: (info) => {
          switch (field as keyof EmployeeRow) {
            case 'email':
              return <Text color="brand.purple">{info.getValue() as string}</Text>;
            case 'status':
              return (
                <Badge colorScheme={`badge.${Status[info.getValue() as IndividualStatus]?.color}`}>
                  {Status[info.getValue() as IndividualStatus]?.value}
                </Badge>
              );
            default:
              return info.getValue();
          }
        },

        header: GiftingEmployeeColumns[field as keyof typeof GiftingEmployeeColumns],
        enableSorting: field !== 'companyName',
        meta: {
          getCellContext: (context: CellContext<EmployeeRow, unknown>) => {
            return {
              onClick: () => actions.profile && actions.profile([context.row.index]),
            };
          },
        },
      });
    }
  );

  columns.unshift({
    id: 'select',
    enableSorting: false,
    header: ({ table }: { table: Table<EmployeeRow> }) => (
      <Checkbox
        {...{
          isChecked: table.getIsAllPageRowsSelected(),
          isIndeterminate: table.getIsSomePageRowsSelected(),
          onChange: table.getToggleAllPageRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }: { row: Row<EmployeeRow> }) => (
      <Checkbox
        {...{
          isChecked: row.getIsSelected(),
          isIndeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      customStyles: { justifyContent: 'center' },
      pin: 'left',
      withBackground: true,
    },
  });

  columns.push(
    columnHelper.display({
      id: 'icons',
      cell: (info) => (
        <HStack justify="end">
          {info.row.original.terminationDate && info.row.original.status === 'LEAVING' && (
            <Tooltip
              hasArrow
              shouldWrapChildren
              arrowPadding={0}
              placement="bottom-end"
              label={`This employee is leaving employment on ${format(
                parseISO(info.row.original.terminationDate),
                DEFAULT_DATE_FORMAT
              )}.`}
            >
              <CalendarOutline boxSize="24px" color="brand.leaving" />
            </Tooltip>
          )}
        </HStack>
      ),
    })
  );

  columns.push(
    columnHelper.display({
      id: 'options',
      meta: {
        getCellContext: (context: CellContext<EmployeeRow, unknown>) => {
          if (context.column.id === 'options') return { style: { padding: 0, textAlign: 'end' } };
          return undefined;
        },
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: (info) => (
        <Menu autoSelect={false} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreCircle boxSize="20px" />}
            variant="menu"
          />
          <Portal>
            <MenuList>
              {Object.keys(actions).flatMap((action, index) => {
                const onActionClick = actions[action as keyof typeof actions];
                if (onActionClick) {
                  const actionValue = giftingEmployeeActionsValues.find(
                    (value) => value.id === action
                  );

                  let isDisabled = false;

                  // TODO: Need to cleanup this logic

                  if (
                    actionValue?.id === EmployeeActionsIds.INVITE &&
                    info.row.original.status !== 'CREATED' &&
                    info.row.original.status !== 'INVITED'
                  ) {
                    return [];
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.INVITE &&
                    info.row.original.status === 'INVITED'
                  ) {
                    isDisabled = true;
                  }

                  if (actionValue?.id === EmployeeActionsIds.SET_UNPAID) {
                    if (info.row.original.onUnpaidLeave) return [];

                    if (
                      !['CREATED', 'INVITED', 'ACTIVE', 'LEAVING'].includes(
                        info.row.original.status
                      )
                    ) {
                      isDisabled = true;
                    }
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.REMOVE_UNPAID &&
                    !info.row.original.onUnpaidLeave
                  ) {
                    return [];
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.TERMINATE &&
                    (info.row.original.status === 'LEAVING' || info.row.original.status === 'LEFT')
                  ) {
                    return [];
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.EDIT_TERMINATE &&
                    info.row.original.status !== 'LEAVING'
                  ) {
                    return [];
                  }

                  return (
                    <Fragment key={action}>
                      {index > 0 && <MenuDivider key={`${info.row.index}${action}`} />}
                      <MenuItem
                        key={action}
                        color="brand.black"
                        onClick={() => onActionClick([info.row.index])}
                        icon={
                          actionValue?.icon && (
                            <Icon as={actionValue.icon} boxSize="14px" color="brand.darkGray" />
                          )
                        }
                        isDisabled={isDisabled}
                      >
                        {actionValue?.value}
                      </MenuItem>
                    </Fragment>
                  );
                }
                return [];
              })}
            </MenuList>
          </Portal>
        </Menu>
      ),
    })
  );

  return columns;
}

export function getGiftingEmployeeBulkActionsValues<TData extends object>(
  actions: GiftingEmployeesBulkActions,
  selectedRows: Row<TData>[]
) {
  const isInviteDisabled = !selectedRows.some((row) =>
    ['CREATED'].includes((row.original as EmployeeRow).status)
  );

  const isGiftDisabled = !selectedRows.some((row) =>
    ['ACTIVE', 'INVITED'].includes((row.original as EmployeeRow).status)
  );

  const bulkActions = {
    ...actions,
    invite: { ...actions.invite, isDisabled: isInviteDisabled },
    gift: { ...actions.gift, isDisabled: isGiftDisabled },
  };

  const bulkActionsElements = Object.keys(bulkActions).map((bulkAction) => {
    const actionValue = giftingEmployeeActionsValues.find((value) => value.id === bulkAction);

    return (
      <Button
        key={bulkAction}
        size="bulk"
        variant="bulk"
        onClick={() =>
          bulkActions[bulkAction as keyof GiftingEmployeesBulkActions].action(
            selectedRows.map((row) => row.index)
          )
        }
        isDisabled={bulkActions[bulkAction as keyof GiftingEmployeesBulkActions].isDisabled}
        _disabled={{ opacity: 1, cursor: 'not-allowed' }}
      >
        <Icon as={actionValue?.icon} boxSize="20px" />
        <Text>{actionValue?.value}</Text>
      </Button>
    );
  });

  return { label: 'employee', bulkActions: bulkActionsElements };
}
